// depositUtils.js
// Deposit gross-down + the shared "does this request already have a deposit?" predicate.
//
// The shop types a TOTAL; Stripe's tax rate is applied to the line item we send, so the
// line must be the PRE-TAX amount.
//
// Two things worth knowing before touching this:
//
// 1. The deposit line is taxed UNCONDITIONALLY at the location's full rate — unlike
//    final-invoice line items, which honour each service's `taxable` flag. Do NOT
//    reintroduce a "taxable ratio" here. The deposit CREDIT on the final invoice is
//    also taxed unconditionally, so deposit-in and credit-out cancel exactly and the
//    job's per-service tax treatment survives untouched. A ratio < 1 protects nothing
//    and just collects more than the shop typed.
//
// 2. `invoiceAmount` on the installer_payments row must be this pre-tax subtotal, not
//    the shop's total. The final invoice's credit reads that column and re-applies the
//    tax rate to the negative line, so storing the tax-inclusive figure double-counts.

/** Round to cents without binary-float drift (1.005 -> 1.01, not 1.00). */
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Split a tax-inclusive total into { subtotal, tax } such that
 * subtotal + tax === total, exactly, to the cent.
 *
 * Naive division misses by a cent on some inputs because the tax Stripe computes is
 * round(subtotal * rate), not total - subtotal. We derive the subtotal, compute the
 * tax the way Stripe will, then nudge the subtotal by the residual and re-verify.
 *
 * @param {number} total   tax-inclusive amount the shop typed
 * @param {number} ratePct tax rate as a percentage (7.25 means 7.25%), 0 for none
 * @returns {{subtotal: number, tax: number}}
 */
export const grossDownToTotal = (total, ratePct) => {
  const target = round2(parseFloat(total) || 0);
  const rate = parseFloat(ratePct) || 0;

  if (target <= 0) return { subtotal: 0, tax: 0 };
  if (rate <= 0) return { subtotal: target, tax: 0 };

  const taxOn = (sub) => round2(sub * (rate / 100));

  let subtotal = round2(target / (1 + rate / 100));
  let tax = taxOn(subtotal);

  // Walk the residual off. One step is enough in practice; the loop bounds it anyway
  // so a pathological rate can't spin.
  for (let i = 0; i < 4 && round2(subtotal + tax) !== target; i++) {
    const residual = round2(target - subtotal - tax);
    subtotal = round2(subtotal + residual);
    tax = taxOn(subtotal);
  }

  // If rounding genuinely can't land on the target (possible at odd rates), absorb the
  // last cent into tax rather than bill the customer a different number than the shop
  // quoted. Stripe recomputes tax itself, so this only affects the row we store.
  if (round2(subtotal + tax) !== target) {
    tax = round2(target - subtotal);
  }

  return { subtotal, tax };
};

/**
 * A deposit payment row that still counts against creating another one.
 *
 * Voided rows deliberately do NOT count — a shop that voids a deposit invoice must be
 * able to issue a replacement. (Request 2868 in production is a voided deposit; without
 * this exclusion it would be blocked forever.)
 */
export const isActiveDepositRow = (p) =>
  !!p && p.payment_type === 'deposit' && p.payment_state !== 'void';

/**
 * Client-side duplicate guard. Load-bearing: the server's deposit branch skips
 * checkExistingBeforeCreate entirely, so every POST /invoices with deposit metadata mints
 * a new Stripe invoice and emails it. Two calls = two live invoices the customer can pay.
 *
 * The payment rows are AUTHORITATIVE when we have them, and `depositStatus` is only a
 * fallback for callers that couldn't fetch them. Two reasons they don't get combined:
 *
 *   - `depositStatus` reads 'none' for every deposit created before this fix, because the
 *     client never wrote a row. It cannot stand alone.
 *   - The server derives it with `COUNT(*) > 0 THEN 'pending'` over all deposit rows,
 *     which counts VOIDED ones. Or-ing it in would re-block the voided-deposit case that
 *     isActiveDepositRow exists to allow.
 *
 * @param {object} request       request object, may carry depositStatus
 * @param {Array}  depositRows   rows from /installerPaymentsRequest/:id (any payment_type)
 */
export const hasExistingDeposit = (request, depositRows) => {
  if (Array.isArray(depositRows)) return depositRows.some(isActiveDepositRow);
  const status = request && request.depositStatus;
  return !!status && status !== 'none';
};
